import { useCallback, useEffect, useMemo, useState } from "react";
import type { Key } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QuestionCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Typography, message } from "antd";
import { StoreIcon } from "lucide-react";
import type { IntegrationProvider, IntegrationSyncStatus, ProductItemIntegrationResponse } from "apiClients/base-generated";
import { useLocaleStore } from "stores/localeStore";
import { IntegrationProductsFiltersCard } from "../../domains/integrations/components/IntegrationProductsFiltersCard";
import { IntegrationLockUntilModal } from "../../domains/integrations/modals/IntegrationLockUntilModal";
import {
  IntegrationNorthmillSyncEditModal,
  NORTHMILL_SYNC_EDIT_MAX_RECORDS,
} from "../../domains/integrations/modals/IntegrationNorthmillSyncEditModal";
import { NorthmillIntegrationInfoModal } from "../../domains/integrations/modals/NorthmillIntegrationInfoModal";
import { IntegrationNorthmillStockImportModal } from "../../domains/integrations/modals/IntegrationNorthmillStockImportModal";
import {
  IntegrationProductsTable,
  type IntegrationRowLocks,
} from "../../domains/integrations/components/IntegrationProductsTable";
import {
  IntegrationSyncStatusCode,
  IntegrationTabs,
} from "../../domains/integrations/components/IntegrationTabs";
import { useDebounce } from "components/hooks/useDebounce";
import { useIntegrationProductsUnlockMutation } from "../../domains/integrations/hooks/useIntegrationProductsUnlockMutation";
import {
  DEFAULT_INTEGRATION_PRODUCTS_PAGE_SIZE,
  useIntegrationProductsQuery,
  type IntegrationProductsActivityFilter,
  type IntegrationProductsLockFilter,
  type IntegrationProductsVariantFilter,
} from "../../domains/integrations/hooks/useIntegrationProductsQuery";
import {
  useIntegrationProductsEnableSyncMutation,
  useIntegrationProductsPauseMutation,
} from "../../domains/integrations/hooks/useIntegrationProductsSyncStatusMutations";
import {
  northmillItemRequestFromRecord,
  useIntegrationNorthmillUpsertMutation,
} from "../../domains/integrations/hooks/useIntegrationNorthmillUpsertMutation";
import {
  resolveIntegrationSyncIdsFromRowKeys,
  resolveProductIdsFromRowKeys,
  resolveRecordsFromRowKeys,
} from "../../domains/integrations/integrationProductRowKey";
import type { IntegrationProductAction } from "../../domains/integrations/integrationProductActions";
import type { IntegrationLockConfirmPayload } from "../../domains/integrations/integrationRowLockTypes";
import styles from "./northmill.module.css";
import { useNorthmillAccountCodesQuery } from "domains/integrations/hooks/useNorthmillAccountCodesQuery";
import { useNorthmillGroupsQuery } from "domains/integrations/hooks/useNorthmillGroupsQuery";

const { Title, Text } = Typography;

const NORTHMILL_INTEGRATION_PROVIDER = 1 as IntegrationProvider;

export const NorthmillPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { activeLanguage } = useLocaleStore();
  const [activeSyncStatus, setActiveSyncStatus] = useState<IntegrationSyncStatus>(
    IntegrationSyncStatusCode.Synced,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [rowLocks, setRowLocks] = useState<IntegrationRowLocks>({});
  const [bulkLockOpen, setBulkLockOpen] = useState(false);
  const [northmillInfoOpen, setNorthmillInfoOpen] = useState(false);
  const [bulkLockKeys, setBulkLockKeys] = useState<string[]>([]);
  const [lockFilter, setLockFilter] = useState<IntegrationProductsLockFilter>("all");
  const [activityFilter, setActivityFilter] = useState<IntegrationProductsActivityFilter>("all");
  const [variantFilter, setVariantFilter] = useState<IntegrationProductsVariantFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [northmillGroupFilter, setNorthmillGroupFilter] = useState("");
  const [integrationPage, setIntegrationPage] = useState(1);
  const [integrationPageSize, setIntegrationPageSize] = useState(DEFAULT_INTEGRATION_PRODUCTS_PAGE_SIZE);
  const [northmillEditRecords, setNorthmillEditRecords] = useState<ProductItemIntegrationResponse[]>([]);
  /** När redigeraren kapats till `NORTHMILL_SYNC_EDIT_MAX_RECORDS`: antal markerade rader innan kapning. */
  const [northmillEditTotalSelected, setNorthmillEditTotalSelected] = useState<number | undefined>(undefined);
  const [northmillStockImportOpen, setNorthmillStockImportOpen] = useState(false);

  useEffect(() => {
    setIntegrationPage(1);
    setSelectedRowKeys([]);
    setRowLocks({});
    setBulkLockKeys([]);
    setBulkLockOpen(false);
    if (activeSyncStatus === IntegrationSyncStatusCode.NotSynced) {
      setLockFilter("all");
      setActivityFilter("all");
    }
  }, [activeSyncStatus]);

  useEffect(() => {
    setIntegrationPage(1);
    setSelectedRowKeys([]);
    setRowLocks({});
    setBulkLockKeys([]);
    setBulkLockOpen(false);
  }, [lockFilter, activityFilter, variantFilter, debouncedSearch, northmillGroupFilter]);

  const {
    data: productIntegrationResponse,
    isFetching,
    isError,
    error,
  } = useIntegrationProductsQuery(
    activeLanguage?.locale,
    NORTHMILL_INTEGRATION_PROVIDER,
    activeSyncStatus,
    {
      lock: lockFilter,
      activity: activityFilter,
      variant: variantFilter,
      search: debouncedSearch,
      northmillGroupId: northmillGroupFilter,
    },
    { page: integrationPage, pageSize: integrationPageSize },
  );

  const integrationTotal = productIntegrationResponse?.total;

  const { data: northmillGroupsPage, isFetching: isFetchingNorthmillGroups } = useNorthmillGroupsQuery(
    activeLanguage?.locale,
  );

  const { data: northmillAccountCodesPage, isFetching: isFetchingNorthmillAccountCodes } =
    useNorthmillAccountCodesQuery(activeLanguage?.locale);

  const northmillGroupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of northmillGroupsPage?.data ?? []) {
      if (g.id == null) continue;
      const id = String(g.id);
      const name = g.name?.trim();
      map.set(id, name && name.length > 0 ? name : id);
    }
    return map;
  }, [northmillGroupsPage?.data]);

  const northmillGroupSelectOptions = useMemo(
    () =>
      (northmillGroupsPage?.data ?? [])
        .filter((g) => g.id != null)
        .map((g) => {
          const id = String(g.id);
          const name = g.name?.trim();
          return {
            value: id,
            label: name && name.length > 0 ? `${name} (${id})` : id,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label, "sv")),
    [northmillGroupsPage?.data],
  );

  const northmillAccountCodeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of northmillAccountCodesPage?.data ?? []) {
      if (c.id == null) continue;
      const id = String(c.id);
      const name = c.name?.trim();
      map.set(id, name && name.length > 0 ? name : id);
    }
    return map;
  }, [northmillAccountCodesPage?.data]);

  const northmillAccountCodeSelectOptions = useMemo(
    () =>
      (northmillAccountCodesPage?.data ?? [])
        .filter((c) => c.id != null)
        .map((c) => {
          const id = String(c.id);
          const name = c.name?.trim();
          return {
            value: id,
            label: name && name.length > 0 ? `${name} (${id})` : id,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label, "sv")),
    [northmillAccountCodesPage?.data],
  );

  const renderNorthmillGroupCell = useCallback(
    (record: ProductItemIntegrationResponse) => {
      const gid = record.northmillGroupId?.trim();
      if (!gid) return "—";
      return northmillGroupNameById.get(gid) ?? gid;
    },
    [northmillGroupNameById],
  );

  const renderNorthmillAccountCodeCell = useCallback(
    (record: ProductItemIntegrationResponse) => {
      const aid = record.northmillAccountCodeId?.trim();
      if (!aid) return "—";
      return northmillAccountCodeNameById.get(aid) ?? aid;
    },
    [northmillAccountCodeNameById],
  );

  useEffect(() => {
    if (integrationTotal == null) return;
    if (integrationTotal === 0) {
      if (integrationPage !== 1) setIntegrationPage(1);
      return;
    }
    const maxPage = Math.max(1, Math.ceil(integrationTotal / integrationPageSize));
    if (integrationPage > maxPage) {
      setIntegrationPage(maxPage);
    }
  }, [integrationTotal, integrationPage, integrationPageSize]);

  const handleIntegrationPaginationChange = useCallback((nextPage: number, nextPageSize: number) => {
    setSelectedRowKeys([]);
    setBulkLockKeys([]);
    setBulkLockOpen(false);
    setIntegrationPage(nextPage);
    setIntegrationPageSize(nextPageSize);
  }, []);

  const unlockMutation = useIntegrationProductsUnlockMutation(activeLanguage?.locale);
  const pauseIntegrationMutation = useIntegrationProductsPauseMutation(activeLanguage?.locale);
  const enableSyncMutation = useIntegrationProductsEnableSyncMutation(activeLanguage?.locale);
  const northmillUpsertMutation = useIntegrationNorthmillUpsertMutation(activeLanguage?.locale);

  const rows = productIntegrationResponse?.data ?? [];

  const handleBulkAction = useCallback(
    (action: IntegrationProductAction, keys: string[]) => {
      if (action === "lock") {
        setBulkLockKeys(keys);
        setBulkLockOpen(true);
        return;
      }
      if (action === "unlock") {
        const integrationSyncIds = resolveIntegrationSyncIdsFromRowKeys(keys, rows);
        if (integrationSyncIds.length !== keys.length) {
          message.error("Kunde inte matcha alla valda rader mot integrationssynk-ID.");
          return;
        }
        if (!integrationSyncIds.length) {
          message.error("Inga rader att låsa upp.");
          return;
        }
        void unlockMutation
          .mutateAsync({
            integrationProvider: NORTHMILL_INTEGRATION_PROVIDER,
            integrationSyncIds,
          })
          .then(() => {
            message.success("Markerade produkter är upplåsta.");
            setRowLocks((prev) => {
              const next = { ...prev };
              keys.forEach((k) => {
                delete next[k];
              });
              return next;
            });
          })
          .catch(() => message.error("Kunde inte låsa upp markerade produkter."));
        return;
      }
      if (action === "pauseIntegration") {
        const productIds = resolveProductIdsFromRowKeys(keys, rows);
        if (productIds == null) {
          message.error("Kunde inte matcha markerade rader till produkt-ID.");
          return;
        }
        void pauseIntegrationMutation
          .mutateAsync({
            integrationProvider: NORTHMILL_INTEGRATION_PROVIDER,
            productItemIds: productIds,
          })
          .then(() => message.success("Integration pausad."))
          .catch(() => message.error("Kunde inte pausa integration."));
        return;
      }
      if (action === "sync") {
        const productIds = resolveProductIdsFromRowKeys(keys, rows);
        if (productIds == null) {
          message.error("Kunde inte matcha markerade rader till produkt-ID.");
          return;
        }
        void enableSyncMutation
          .mutateAsync({
            integrationProvider: NORTHMILL_INTEGRATION_PROVIDER,
            productItemIds: productIds,
          })
          .then(() => message.success("Integration startad."))
          .catch(() => message.error("Kunde inte starta integration."));
        return;
      }
      if (action === "editIntegrationSync") {
        const recs = resolveRecordsFromRowKeys(keys, rows);
        if (recs.length !== keys.length) {
          message.error("Kunde inte matcha alla valda rader.");
          return;
        }
        if (!recs.length) {
          message.error("Välj minst en rad att redigera.");
          return;
        }
        if (recs.some((r) => r.id == null)) {
          message.error("En eller fler rader saknar integrationssynk-ID.");
          return;
        }
        if (recs.some((r) => !r.itemNo?.trim())) {
          message.error("En eller fler rader saknar artikelnummer.");
          return;
        }
        const total = recs.length;
        if (total > NORTHMILL_SYNC_EDIT_MAX_RECORDS) {
          message.warning(
            `Endast de första ${NORTHMILL_SYNC_EDIT_MAX_RECORDS} av ${total} markerade raderna öppnas i redigeraren.`,
          );
          setNorthmillEditTotalSelected(total);
          setNorthmillEditRecords(recs.slice(0, NORTHMILL_SYNC_EDIT_MAX_RECORDS));
        } else {
          setNorthmillEditTotalSelected(undefined);
          setNorthmillEditRecords(recs);
        }
        return;
      }
    },
    [rows, pauseIntegrationMutation, enableSyncMutation, unlockMutation],
  );

  const handleRowIntegrationAction = useCallback(
    (action: IntegrationProductAction, record: ProductItemIntegrationResponse, rowKey: string) => {
      if (action === "lock") return;
      if (action === "editIntegrationSync") {
        if (record.id == null) {
          message.error("Saknar integrationssynk-ID för denna rad.");
          return;
        }
        if (!record.itemNo?.trim()) {
          message.error("Saknar artikelnummer för denna rad.");
          return;
        }
        setNorthmillEditTotalSelected(undefined);
        setNorthmillEditRecords([record]);
        return;
      }
      if (action !== "pauseIntegration" && action !== "sync") return;
      const productIds = resolveProductIdsFromRowKeys([rowKey], rows);
      if (productIds == null) {
        message.error("Kunde inte matcha raden till produkt-ID.");
        return;
      }
      if (action === "pauseIntegration") {
        void pauseIntegrationMutation
          .mutateAsync({
            integrationProvider: NORTHMILL_INTEGRATION_PROVIDER,
            productItemIds: productIds,
          })
          .then(() => message.success("Integration pausad."))
          .catch(() => message.error("Kunde inte pausa integration."));
        return;
      }
      void enableSyncMutation
        .mutateAsync({
          integrationProvider: NORTHMILL_INTEGRATION_PROVIDER,
          productItemIds: productIds,
        })
        .then(() => message.success("Integration startad."))
        .catch(() => message.error("Kunde inte starta integration."));
    },
    [rows, pauseIntegrationMutation, enableSyncMutation],
  );

  const errorMessage =
    isError && error instanceof Error ? error.message : isError ? String(error) : null;

  const [northmillPageRefreshing, setNorthmillPageRefreshing] = useState(false);

  const handleRefreshNorthmillPage = useCallback(async () => {
    setNorthmillPageRefreshing(true);
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["integration-products"] }),
        queryClient.refetchQueries({ queryKey: ["northmill-groups"] }),
        queryClient.refetchQueries({ queryKey: ["northmill-account-codes"] }),
      ]);
    } finally {
      setNorthmillPageRefreshing(false);
    }
  }, [queryClient]);

  return (
    <Flex vertical gap={16}>
      <Card bordered={false} className="criclebox">
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
            <Flex align="center" gap={12}>
              <span
                style={{
                  color: "rgba(0, 0, 0, 0.45)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  lineHeight: 0,
                  marginBottom: "12px",
                }}
                aria-hidden
              >
                <StoreIcon size={24} strokeWidth={1.75} absoluteStrokeWidth />
              </span>
              <Title level={4} style={{ marginBottom: 0 }}>
                Northmill Point of Sale
              </Title>
            </Flex>
            <Flex align="center" gap={4} wrap="wrap">
              <Text type="secondary" style={{ fontSize: "0.875rem" }}>
                Northmill Bank Integration
              </Text>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                loading={northmillPageRefreshing}
                onClick={() => void handleRefreshNorthmillPage()}
                aria-label="Uppdatera lista och filterdata"
                title="Uppdatera"
              />
              <Button
                type="text"
                size="small"
                icon={<QuestionCircleOutlined />}
                onClick={() => setNorthmillInfoOpen(true)}
                aria-label="Information om Northmill-integrationen"
                style={{ color: "rgba(0, 0, 0, 0.45)" }}
              />
            </Flex>
          </Flex>

          <IntegrationTabs
            activeStatus={activeSyncStatus}
            onChange={setActiveSyncStatus}
            countBySyncStatus={productIntegrationResponse?.countBySyncStatus}
            className={styles.northmillSyncTabs}
            onAddArticlesForSync={() => setNorthmillStockImportOpen(true)}
          />
        </Flex>
      </Card>

      <IntegrationProductsFiltersCard
        activeSyncStatus={activeSyncStatus}
        search={searchInput}
        onSearchChange={setSearchInput}
        variantFilter={variantFilter}
        onVariantFilterChange={setVariantFilter}
        lockFilter={lockFilter}
        onLockFilterChange={setLockFilter}
        activityFilter={activityFilter}
        onActivityFilterChange={setActivityFilter}
        northmillGroupId={northmillGroupFilter}
        onNorthmillGroupIdChange={setNorthmillGroupFilter}
        northmillGroupOptions={northmillGroupSelectOptions}
        northmillGroupsLoading={isFetchingNorthmillGroups}
      />

      {errorMessage && (
        <Alert type="error" message="Request failed" description={errorMessage} showIcon />
      )}

      <NorthmillIntegrationInfoModal open={northmillInfoOpen} onCancel={() => setNorthmillInfoOpen(false)} />

      <IntegrationNorthmillStockImportModal
        open={northmillStockImportOpen}
        onCancel={() => setNorthmillStockImportOpen(false)}
        groupOptions={northmillGroupSelectOptions}
        groupsLoading={isFetchingNorthmillGroups}
        confirmLoading={northmillUpsertMutation.isLoading}
        onImport={async (items) => {
          try {
            await northmillUpsertMutation.mutateAsync({ items });
            setActiveSyncStatus(IntegrationSyncStatusCode.Synced);
            setIntegrationPage(1);
            setSelectedRowKeys([]);
            setRowLocks({});
            setBulkLockKeys([]);
            setBulkLockOpen(false);
            await queryClient.refetchQueries({ queryKey: ["integration-products"] });
            message.success(
              items.length === 1 ? "En artikel importerades." : `${items.length} artiklar importerades.`,
            );
          } catch {
            message.error("Kunde inte importera. Försök igen.");
            throw new Error("Northmill stock import failed");
          }
        }}
      />

      <IntegrationNorthmillSyncEditModal
        open={northmillEditRecords.length > 0}
        records={northmillEditRecords}
        totalSelectedBeforeCap={northmillEditTotalSelected}
        groupOptions={northmillGroupSelectOptions}
        groupsLoading={isFetchingNorthmillGroups}
        accountCodeOptions={northmillAccountCodeSelectOptions}
        accountCodesLoading={isFetchingNorthmillAccountCodes}
        onCancel={() => {
          setNorthmillEditRecords([]);
          setNorthmillEditTotalSelected(undefined);
        }}
        confirmLoading={northmillUpsertMutation.isLoading}
        onSave={async (changedRows) => {
          if (!changedRows.length) return;
          try {
            await northmillUpsertMutation.mutateAsync({
              items: changedRows.map((row) => ({
                itemNo: row.itemNo,
                northmillGroupId: row.northmillGroupId,
                northmillAccountCodeId: row.northmillAccountCodeId,
                isInactive: row.isInactive,
                lockedUntil: row.lockedUntil,
                northmillLockedQuantity: row.northmillLockedQuantity,
              })),
            });
            message.success(
              changedRows.length === 1
                ? "Northmill-integration uppdaterad."
                : `Uppdaterade ${changedRows.length} artiklar.`,
            );
            setSelectedRowKeys([]);
          } catch {
            message.error("Kunde inte spara ändringar.");
            throw new Error("Northmill upsert failed");
          }
        }}
      />

      <IntegrationLockUntilModal
        open={bulkLockOpen}
        onCancel={() => setBulkLockOpen(false)}
        confirmLoading={northmillUpsertMutation.isLoading}
        onConfirm={async ({ lockedUntilIso, pausedStockQuantity }: IntegrationLockConfirmPayload) => {
          const lockRecords = resolveRecordsFromRowKeys(bulkLockKeys, rows);
          if (lockRecords.length !== bulkLockKeys.length) {
            message.error("Kunde inte matcha alla valda rader.");
            throw new Error("Missing records");
          }
          if (lockRecords.some((r) => !r.itemNo?.trim())) {
            message.error("En eller fler rader saknar artikelnummer.");
            throw new Error("Missing item numbers");
          }
          try {
            await northmillUpsertMutation.mutateAsync({
              items: lockRecords.map((r) =>
                northmillItemRequestFromRecord(r, {
                  lockedUntil: lockedUntilIso,
                  northmillLockedQuantity: pausedStockQuantity,
                }),
              ),
            });
          } catch {
            message.error("Kunde inte uppdatera synkpaus för markerade rader.");
            throw new Error("Bulk lock mutation failed");
          }
          const entry = { lockedUntil: lockedUntilIso, pausedStockQuantity };
          setRowLocks((prev) => {
            const next = { ...prev };
            bulkLockKeys.forEach((k) => {
              next[k] = entry;
            });
            return next;
          });
        }}
        title="Lås markerade rader"
      />

      <Card>
        <IntegrationProductsTable
          dataSource={rows}
          northmillGroupLabel={renderNorthmillGroupCell}
          northmillAccountCodeLabel={renderNorthmillAccountCodeCell}
          pagination={
            integrationTotal != null
              ? {
                  current: integrationPage,
                  pageSize: integrationPageSize,
                  total: integrationTotal,
                  onChange: handleIntegrationPaginationChange,
                }
              : undefined
          }
          loading={isFetching}
          showRowLockColumn={activeSyncStatus !== IntegrationSyncStatusCode.NotSynced}
          lockConfirmLoading={northmillUpsertMutation.isLoading || unlockMutation.isLoading}
          onRowAction={handleRowIntegrationAction}
          rowLocks={rowLocks}
          onRowLockChange={(rowKey, entry, record) => {
            if (entry == null) {
              const syncId = record.id;
              if (syncId == null) {
                message.error("Saknar integrationssynk-ID för denna rad.");
                return Promise.reject(new Error("Missing integration sync id"));
              }
              return unlockMutation
                .mutateAsync({
                  integrationProvider: NORTHMILL_INTEGRATION_PROVIDER,
                  integrationSyncIds: [syncId],
                })
                .then(() => {
                  setRowLocks((prev) => {
                    const next = { ...prev };
                    delete next[rowKey];
                    return next;
                  });
                })
                .catch(() => {
                  message.error("Kunde inte låsa upp produkt.");
                  throw new Error("Unlock mutation failed");
                });
            }
            const syncId = record.id;
            if (syncId == null) {
              message.error("Saknar integrationssynk-ID för denna rad.");
              return Promise.reject(new Error("Missing integration sync id"));
            }
            if (!record.itemNo?.trim()) {
              message.error("Saknar artikelnummer för denna rad.");
              return Promise.reject(new Error("Missing item number"));
            }
            return northmillUpsertMutation
              .mutateAsync({
                items: [
                  northmillItemRequestFromRecord(record, {
                    lockedUntil: entry.lockedUntil,
                    northmillLockedQuantity: entry.pausedStockQuantity,
                  }),
                ],
              })
              .then(() => {
                setRowLocks((prev) => ({ ...prev, [rowKey]: entry }));
              })
              .catch(() => {
                message.error("Kunde inte uppdatera synkpaus.");
                throw new Error("Lock mutation failed");
              });
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          bulkActions={{
            selectedCount: selectedRowKeys.length,
            selectedRowKeys,
            onBulkAction: handleBulkAction,
          }}
        />
      </Card>
    </Flex>
  );
};
